import React from 'react';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { LiquidGlassCard } from '@/components/liquid-glass/LiquidGlassCard';
import {
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  SparklesIcon,
  MegaphoneIcon,
  PaperAirplaneIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const quickActions = [
  {
    title: 'Draft a press release',
    description: 'Key announcement details',
    icon: DocumentTextIcon,
    bgColor: 'bg-primary/10',
    color: 'text-primary',
    template: 'Draft a press release for [topic]. Include key messages, quotes, and a boilerplate.',
  },
  {
    title: 'Polish my pitch',
    description: 'Improve clarity & impact',
    icon: SparklesIcon,
    bgColor: 'bg-secondary/10',
    color: 'text-secondary',
    template: 'Help me polish this media pitch to improve clarity, tone, and impact:\n\n[paste your pitch here]',
  },
  {
    title: 'Announce on social',
    description: 'LinkedIn and X posts',
    icon: MegaphoneIcon,
    bgColor: 'bg-accent/10',
    color: 'text-accent',
    template: 'Create social media posts for LinkedIn and X announcing [topic]. Keep it engaging and professional.',
  },
  {
    title: 'Send to press',
    description: 'Targeted media email',
    icon: PaperAirplaneIcon,
    bgColor: 'bg-primary/10',
    color: 'text-primary',
    template: 'Draft a targeted media pitch email for [journalist/outlet] about [topic]. Make it concise and newsworthy.',
  },
  {
    title: 'Crisis response',
    description: 'Rapid response plan',
    icon: ExclamationTriangleIcon,
    bgColor: 'bg-red-500/10',
    color: 'text-red-500',
    template: 'Help me draft a crisis communication response for [situation]. Include a holding statement and key talking points.',
  },
];

interface EmptyStateProps {
  activeClient?: any;
  onQuickAction?: (template: string) => void;
}

export function EmptyState({ activeClient, onQuickAction }: EmptyStateProps) {
  const clientName = activeClient?.name;

  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center max-w-2xl space-y-8">
        {/* Animated Icon */}
        <div className="relative mx-auto w-24 h-24">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-secondary/20 to-accent/20 blur-3xl animate-pulse" />
          <div className="relative w-24 h-24 bg-gradient-to-br from-primary to-secondary rounded-3xl flex items-center justify-center shadow-2xl">
            <ChatBubbleLeftRightIcon className="h-12 w-12 text-white" />
          </div>
        </div>

        {/* Heading */}
        <div>
          <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            {clientName
              ? `Ready to create for ${clientName}?`
              : 'Ready to revolutionize your PR?'}
          </h2>
          <p className="text-lg text-muted-foreground mt-3 max-w-lg mx-auto">
            I'm your AI-powered PR assistant. Ask me anything about press releases, media pitches, or client communications.
          </p>
        </div>

        {/* Quick Action Cards */}
        <div className="grid grid-cols-2 gap-3 max-w-xl mx-auto">
          {quickActions.map((action) => (
            <LiquidGlassCard
              key={action.title}
              variant="default"
              className="cursor-pointer px-4 py-4 text-center"
              onClick={() => onQuickAction?.(action.template)}
            >
              <div
                className={cn(
                  'w-9 h-9 rounded-xl flex items-center justify-center mx-auto mb-2',
                  action.bgColor
                )}
              >
                <action.icon className={cn('h-4.5 w-4.5', action.color)} />
              </div>
              <h3 className="font-semibold text-sm">{action.title}</h3>
              <p className="text-xs text-muted-foreground mt-1">{action.description}</p>
            </LiquidGlassCard>
          ))}
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button size="lg" variant="glow" asChild>
            <Link href="/tools">
              <DocumentTextIcon className="h-5 w-5" />
              Explore PR Tools
            </Link>
          </Button>
          <Button size="lg" variant="outline" asChild>
            <Link href="/clients">
              <UserGroupIcon className="h-5 w-5" />
              Manage Clients
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

export default EmptyState;
